import type { ForwardedRef, InputHTMLAttributes } from "react";
import { forwardRef } from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import type { Size } from "./Inputs";
import { DEFAULT_FONT_SIZE, Label, getClassesBySize } from "./Inputs";
import { Text } from "./Text";

export type TextAreaProps = {
  fontSize?: Size;
  error?: string;
  label?: string;
  labelPosition?: "top" | "left";
} & InputHTMLAttributes<HTMLTextAreaElement>;

const baseTextAreaClasses =
  "appearance-none border border-gray-800/20 h-36 rounded-xl w-full py-4 px-4 text-gray-800 placeholder:text-gray-800/30 focus:outline-none hover:border-primary-100 focus-within:border-primary-100 active:border-primary-100";

const outlineInputClasses = "ring-primary-100 transition-all duration-75 focus-within:ring-1 hover:ring-1 focus:ring-1";

const errorTextAreaClasses = "border-error-100 shadow-sm";

export const TextArea = forwardRef(
  (
    { className, fontSize = DEFAULT_FONT_SIZE, labelPosition = "left", error, label, ...props }: TextAreaProps,
    ref: ForwardedRef<HTMLTextAreaElement>,
  ) => (
    <div className={clsx(labelPosition === "top" && "flex flex-col")}>
      {label ? <Label label={label} id={props.id} disabled={props.disabled} /> : null}
      <div className="relative">
        <textarea
          ref={ref}
          {...props}
          className={twMerge(
            baseTextAreaClasses,
            outlineInputClasses,
            getClassesBySize(fontSize),
            error ? errorTextAreaClasses : "",
            className,
          )}
        />
        {props.maxLength ? (
          <Text size="sm" className="absolute right-3 bottom-3 text-gray-800/70">
            {props.value && typeof props.value !== "number" ? props.value.length : 0}/{props.maxLength}
          </Text>
        ) : null}
      </div>
    </div>
  ),
);

TextArea.displayName = "TextArea";
